import React from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { card, inputStyle, EmptyState, Skeleton, I } from '@/components/ui';
import { EnteteEcran } from '@/components/EnteteEcran';

import { Bouton } from '@/shared/Bouton';
import { enTeteCollant } from '@/shared/Tableau';
/**
 * ⚠️ LE SYSTÈME DES DEUX ÉQUIPES A ÉTÉ RETIRÉ DE CET ÉCRAN (RQ, 02/09/2026) —
 * « finalement le système d'équipes ne tient pas, supprime-le totalement ».
 *
 * Il portait une composition par année, une reprise sur l'année précédente et
 * un contrôle d'équilibre de charge. La colonne « Assuré par » ne propose donc
 * plus une équipe à choisir : elle AFFICHE qui est inscrit au planning ce
 * jour-là. C'est le même geste que pour n'importe quel autre jour, et il n'y a
 * plus de second registre à tenir à jour à côté.
 */

interface Creneau { posteCode: string; debut: string | null; fin: string | null; effectif: number }
interface Trame { cle: string; libelle: string; note: string | null; creneaux: Creneau[] }
interface Ligne {
  date: string; libelle: string; regime: string; personnels: string;
  effectifPlanifie: number; recuperationLe: string | null; manque: string;
  trameCle: string | null; trameLibelle: string | null;
}
interface Reponse {
  annee: number;
  lignes: Ligne[];
  trames: Trame[];
  bilan: { total: number; incomplets: number; tenus: number; resume: string };
}
interface Poste { code: string; libelle: string; plageDebut: string | null; plageFin: string | null }

const REGIMES: { k: string; label: string }[] = [
  { k: 'paye', label: 'Chômé et payé' },
  { k: 'nonpaye', label: 'Chômé et non payé' },
  { k: 'garde', label: 'Garde exceptionnelle' },
  { k: 'recuperation', label: 'Jour de récupération' },
];

export function RhFeriesEcran() {
  const { session, showToast } = useGed();
  const [annee, setAnnee] = React.useState(new Date().getFullYear());
  const [d, setD] = React.useState<Reponse | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [tour, setTour] = React.useState(0);
  const [postes, setPostes] = React.useState<Poste[]>([]);
  const [brouillon, setBrouillon] = React.useState<Trame[] | null>(null);
  /** Les trames telles que LUES à l'ouverture du brouillon (14/10/2026), figées :
   *  envoyées en `base`, le serveur n'applique que les trames qu'on a changées. */
  const [baseTrames, setBaseTrames] = React.useState<Trame[] | null>(null);
  // Toute la gestion du planning relève du seul RH (RQ, 03/10/2026).
  const estRH = (session.roles || []).includes('rh');

  React.useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/rh/feries/annee/${annee}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((x) => { if (vivant) { setD(x as Reponse); setErreur(''); } })
      /* Une erreur de lecture ne doit pas se lire « aucun férié » : c'est le
         faux vide que tout le module s'attache à éviter. */
      .catch(() => { if (vivant) setErreur('Le calendrier n’a pas pu être lu — la liste ci-dessous serait fausse.'); });
    return () => { vivant = false; };
  }, [annee, tour]);

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/postes', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((x) => { if (vivant) setPostes((x.postes || []) as Poste[]); })
      /* Sans la liste des postes on ne PROPOSE rien à ajouter — mais on
         continue d'afficher les trames existantes, qui restent vraies. */
      .catch(() => { if (vivant) setPostes([]); });
    return () => { vivant = false; };
  }, []);

  const enregistrerTrames = async (trames: Trame[]) => {
    const r = await fetchEcriture('/api/ged/ged/rh/feries/trames', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(baseTrames ? { trames, base: baseTrames } : { trames }),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok) { showToast(j?.error?.message || 'Enregistrement refusé', 'erreur'); return; }
    setBrouillon(null); setBaseTrames(null); setTour((n) => n + 1);
    showToast('Trames enregistrées.');
  };

  const majFerie = async (l: Ligne, patch: Record<string, unknown>) => {
    const r = await fetchEcriture('/api/ged/ged/rh/feries', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        date: l.date, libelle: l.libelle, regime: l.regime,
        trameCle: l.trameCle, recuperationLe: l.recuperationLe, ...patch,
        /* `base` (14/10/2026) : la ligne telle que lue — le serveur n'écrit
           que le champ changé (fusion à trois voies). */
        base: { libelle: l.libelle, regime: l.regime, trameCle: l.trameCle, recuperationLe: l.recuperationLe },
      }),
    });
    if (!r.ok) { const j = await r.json().catch(() => null); showToast(j?.error?.message || 'Refusé', 'erreur'); return; }
    setTour((n) => n + 1);
  };

  if (erreur) {
    return (
      <section>
        <div style={{ ...card, padding: 16, background: 'var(--amber-50)', border: '1px solid var(--amber-500)' }}>
          <strong style={{ fontSize: 13, color: 'var(--amber-600)' }}>⚠ {erreur}</strong>
        </div>
      </section>
    );
  }
  if (!d) return <div style={{ ...card, padding: 0 }}><Skeleton rows={6} height={36} /></div>;

  const trames = d.trames || [];

  return (
    <section>
      <EnteteEcran rubrique={<>Plannings &amp; RH</>} clauseISO="6.2" titre="Jours fériés"
        sousTitre={<>
          Qui travaille un jour férié se lit <strong>au planning</strong>, comme n’importe quel
          autre jour. Le tableau ci-dessous est un enregistrement qualité — chaque ligne dit ce
          qui lui manque plutôt que de laisser une case vide.
        </>} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0', flexWrap: 'wrap' }}>
        <Bouton taille="sm" onClick={() => setAnnee((a) => a - 1)}>
          <I n="chevron-left" s={14} /> {annee - 1}
        </Bouton>
        <strong style={{ fontSize: 15, minWidth: 60, textAlign: 'center' }}>{annee}</strong>
        <Bouton taille="sm" onClick={() => setAnnee((a) => a + 1)}>
          {annee + 1} <I n="chevron-right" s={14} />
        </Bouton>
        <span style={{ flex: 1 }} />
        <Bouton taille="sm" onClick={() => window.print()}>
          <I n="print" s={14} /> Imprimer
        </Bouton>
      </div>

      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.55 }}>
        {d.bilan.resume}
      </p>

      {/* ══ LES TRAMES ═══════════════════════════════════════════════════
          De quels postes le laboratoire a besoin un jour férié. Une trame dit
          QUELS POSTES ; qui les tient se lit au planning. */}
      <div style={{ ...card, padding: 16, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <strong style={{ flex: 1, fontSize: 13.5 }}>Trames de jour férié</strong>
          {estRH && !brouillon && (
            <Bouton taille="sm" onClick={() => { setBaseTrames(trames); setBrouillon(trames.map((t) => ({ ...t, creneaux: t.creneaux.map((c) => ({ ...c })) }))); }}>
              Modifier
            </Bouton>
          )}
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.55 }}>
          Une trame déclarée <strong>ferme tout poste qui n’y figure pas</strong> ce jour-là. Un
          férié sans trame retombe sur la politique de chaque poste. Les trames ne sont pas rangées
          par année : ce qui s’est réellement passé un férié donné se lit au planning.
        </p>

        {!trames.length && !brouillon ? (
          <EmptyState titre="Aucune trame"
            indice="Sans trame, chaque poste décide seul s’il ouvre un férié. Une trame dit, en un endroit, de quels postes vous avez besoin." />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
            {(brouillon || trames).map((t, i) => (
              <div key={t.cle || i} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: 12 }}>
                {brouillon ? (
                  <>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                      <input style={{ ...inputStyle, flex: 1, height: 28 }} value={t.libelle} placeholder="Libellé"
                        onChange={(e) => setBrouillon((s2) => s2!.map((x, xi) => (xi === i ? { ...x, libelle: e.target.value } : x)))} />
                      <Bouton taille="sm" aria-label={`Supprimer ${t.libelle}`} onClick={() => setBrouillon((s2) => s2!.filter((_, xi) => xi !== i))}>×</Bouton>
                    </div>
                    <input style={{ ...inputStyle, width: '100%', height: 26, fontSize: 12, marginBottom: 8 }}
                      value={t.note || ''} placeholder="Ce que la trame couvre, en une phrase"
                      onChange={(e) => setBrouillon((s2) => s2!.map((x, xi) => (xi === i ? { ...x, note: e.target.value } : x)))} />
                  </>
                ) : (
                  <>
                    <strong style={{ fontSize: 13 }}>{t.libelle}</strong>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 8px' }}>
                      {t.creneaux.length} poste(s) · {t.creneaux.reduce((n, c) => n + (Number(c.effectif) || 0), 0)} personne(s)
                      {t.note ? ` — ${t.note}` : ''}
                    </div>
                  </>
                )}

                {t.creneaux.map((c, k) => (
                  <div key={`${c.posteCode}-${k}`} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                    {brouillon ? (
                      <>
                        <select style={{ ...inputStyle, flex: 1, height: 26, fontSize: 12 }} value={c.posteCode}
                          onChange={(e) => setBrouillon((s2) => s2!.map((x, xi) => (xi !== i ? x
                            : { ...x, creneaux: x.creneaux.map((y, yi) => (yi === k ? { ...y, posteCode: e.target.value } : y)) })))}>
                          {!postes.some((p) => p.code === c.posteCode) && <option value={c.posteCode}>{c.posteCode} — inconnu</option>}
                          {postes.map((p) => <option key={p.code} value={p.code}>{p.libelle}</option>)}
                        </select>
                        <input type="time" style={{ ...inputStyle, width: 92, height: 26, fontSize: 12 }} value={c.debut || ''}
                          onChange={(e) => setBrouillon((s2) => s2!.map((x, xi) => (xi !== i ? x
                            : { ...x, creneaux: x.creneaux.map((y, yi) => (yi === k ? { ...y, debut: e.target.value } : y)) })))} />
                        <input type="time" style={{ ...inputStyle, width: 92, height: 26, fontSize: 12 }} value={c.fin || ''}
                          onChange={(e) => setBrouillon((s2) => s2!.map((x, xi) => (xi !== i ? x
                            : { ...x, creneaux: x.creneaux.map((y, yi) => (yi === k ? { ...y, fin: e.target.value } : y)) })))} />
                        <input type="number" min={1} style={{ ...inputStyle, width: 56, height: 26, fontSize: 12 }} value={c.effectif}
                          onChange={(e) => setBrouillon((s2) => s2!.map((x, xi) => (xi !== i ? x
                            : { ...x, creneaux: x.creneaux.map((y, yi) => (yi === k ? { ...y, effectif: Number(e.target.value) } : y)) })))} />
                        <Bouton taille="sm" aria-label="Retirer ce poste" onClick={() => setBrouillon((s2) => s2!.map((x, xi) => (xi !== i ? x
                            : { ...x, creneaux: x.creneaux.filter((_, yi) => yi !== k) })))}>×</Bouton>
                      </>
                    ) : (
                      <span style={{ fontSize: 12.5 }}>
                        {postes.find((p) => p.code === c.posteCode)?.libelle || c.posteCode}
                        <span style={{ color: 'var(--text-muted)' }}>
                          {c.debut && c.fin ? ` · ${c.debut}–${c.fin}` : ' · horaires du poste'}
                          {c.effectif > 1 ? ` · ${c.effectif} places` : ''}
                        </span>
                      </span>
                    )}
                  </div>
                ))}

                {brouillon && (
                  <select style={{ ...inputStyle, width: '100%', height: 28, marginTop: 6 }} value=""
                    onChange={(e) => {
                      const code = e.target.value; if (!code) return;
                      const p = postes.find((x) => x.code === code);
                      setBrouillon((s2) => s2!.map((x, xi) => (xi !== i ? x
                        : { ...x, creneaux: [...x.creneaux, { posteCode: code, debut: p?.plageDebut || null, fin: p?.plageFin || null, effectif: 1 }] })));
                    }}>
                    <option value="">+ ajouter un poste…</option>
                    {postes.filter((p) => !t.creneaux.some((c) => c.posteCode === p.code))
                      .map((p) => <option key={p.code} value={p.code}>{p.libelle}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        )}

        {brouillon && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Bouton variante="primaire" onClick={() => void enregistrerTrames(brouillon)}>Enregistrer</Bouton>
            <Bouton taille="sm" onClick={() => setBrouillon((s2) => [...s2!, {
              cle: `trame${s2!.length + 1}-${Date.now().toString(36)}`, libelle: 'Nouvelle trame', note: null, creneaux: [],
            }])}>+ Nouvelle trame</Bouton>
            <Bouton taille="sm" onClick={() => setBrouillon(null)}>Annuler</Bouton>
          </div>
        )}
      </div>

      {!d.lignes.length ? (
        <EmptyState titre={`Aucun jour férié enregistré pour ${annee}`}
          indice="Les fêtes religieuses sont lunaires et annoncées tardivement : le calendrier se saisit, il ne se calcule pas." />
      ) : (
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table ref={enTeteCollant} className="ged-tableau" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: 'var(--gray-25)' }}>
                {['Date', 'Jour férié', 'Régime', 'Trame', 'Au planning', 'Récupération', 'Ce qui manque'].map((h) => (
                  <th scope="col" key={h} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'start', borderBottom: '1px solid var(--border-default)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.lignes.map((l) => (
                <tr key={l.date} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{l.date}</td>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{l.libelle}</td>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                    {estRH ? (
                      <select style={{ ...inputStyle, height: 26, fontSize: 12 }} value={l.regime}
                        onChange={(e) => void majFerie(l, { regime: e.target.value })}>
                        {REGIMES.map((r) => <option key={r.k} value={r.k}>{r.label}</option>)}
                      </select>
                    ) : REGIMES.find((r) => r.k === l.regime)?.label}
                  </td>
                  {/* LA TRAME — de quels postes on a besoin ce jour-là.
                      ⚠️ Sans objet en « non payé » : tout le monde travaille,
                      il n'y a pas de sélection de postes à faire. */}
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                    {l.regime === 'nonpaye' ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                      : estRH ? (
                        <select style={{ ...inputStyle, height: 26, fontSize: 12 }} value={l.trameCle || ''}
                          onChange={(e) => void majFerie(l, { trameCle: e.target.value })}>
                          <option value="">— aucune —</option>
                          {trames.map((t) => <option key={t.cle} value={t.cle}>{t.libelle}</option>)}
                        </select>
                      ) : (l.trameLibelle || '—')}
                  </td>
                  {/* ⚠️ LU, jamais saisi : la colonne rend les affectations du
                      jour. La rendre modifiable ici rouvrirait un second endroit
                      où l'on dit qui travaille — c'est exactement ce que le
                      retrait des équipes ferme. */}
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{l.personnels}</td>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                    {estRH && l.regime === 'paye' ? (
                      <input type="date" style={{ ...inputStyle, height: 26, fontSize: 12 }} value={l.recuperationLe || ''}
                        onChange={(e) => void majFerie(l, { recuperationLe: e.target.value })} />
                    ) : (l.recuperationLe || '—')}
                  </td>
                  {/* ⚠️ CE QUI MANQUE, sur la ligne même. Une case vide sur un
                      tableau imprimé se lit « personne n'est requis ». */}
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', color: l.manque ? 'var(--amber-600)' : 'var(--text-muted)' }}>
                    {l.manque || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
